import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Generic hook for loading modular translations for any page/feature
 * 
 * @param moduleName - The name of the module (e.g., 'bank', 'kyc', 'recipients')
 * @returns Translation function and loading state
 * 
 * @example
 * const { mt } = useModuleTranslations('bank');
 * return <h1>{mt('page.TITLE')}</h1>;
 */
export const useModuleTranslations = (moduleName: string) => {
  const { t, i18n } = useTranslation();
  const [moduleTranslations, setModuleTranslations] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadModuleTranslations = async () => {
      try {
        
        // Normalize language like "es-ES" -> "es"
        const currentLanguage = (i18n.language || 'en'||'es').split('-')[0];
        const response = await fetch(`/assets/i18n/translations/${currentLanguage}/${moduleName}.json`);
        
        if (response.ok) {
          const translations = await response.json();
          setModuleTranslations(translations);
        } else {
          console.warn(`${moduleName} translations not found, using fallback`);
        }
      } catch (error) {
        console.warn(`Failed to load ${moduleName} translations:`, error);
      } finally {
        setLoading(false);
      }
    };

    loadModuleTranslations();
  }, [moduleName, i18n.language]);

  /**
   * Module translation function
   * Tries modular translations first, then falls back to main translations
   */
  const mt = (key: string, fallbackKey?: string) => {
    // While loading, avoid calling i18next to prevent missingKey spam
    if (loading) return '';

    // Try modular translations first
    if (moduleTranslations) {
      const keys = key.split('.');
      let value = moduleTranslations;
      
      for (const k of keys) {
        if (value && typeof value === 'object' && k in value) {
          value = value[k];
        } else {
          value = undefined;
          break;
        }
      }
      
      if (value && typeof value === 'string') {
        return value;
      }
    }
    
    // Fallback to main translations
    // If fallbackKey provided, use it; otherwise construct from moduleName.key
    return t(fallbackKey || `${moduleName}.${key}`);
  };

  return { mt, loading };
};